export interface WikiDraftPayload {
  title: string;
  type: string;
  summary: string;
  content: string;
  tags: string[];
  templateData: Record<string, string>;
}

const asTrimmedString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const normalizeTags = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [];
};

const normalizeTemplateData = (value: unknown): Record<string, string> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const record = value as Record<string, unknown>;
  const output: Record<string, string> = {};
  for (const [key, raw] of Object.entries(record)) {
    if (!key.trim()) continue;
    if (typeof raw === 'string') {
      const normalized = raw.trim();
      if (normalized) output[key] = normalized;
      continue;
    }
    if (typeof raw === 'number' || typeof raw === 'boolean') {
      output[key] = String(raw);
    }
  }
  return output;
};

const tryParseObject = (candidate: string): Record<string, unknown> | null => {
  const trimmed = candidate.trim();
  if (!trimmed) return null;
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return null;
  }
  return null;
};

const extractJsonCandidates = (raw: string): string[] => {
  const trimmed = raw.trim();
  if (!trimmed) return [];

  const candidates: string[] = [trimmed];
  const fencedMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fencedMatch?.[1]) {
    candidates.unshift(fencedMatch[1]);
  }

  const firstBrace = trimmed.indexOf('{');
  const lastBrace = trimmed.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace > firstBrace) {
    candidates.unshift(trimmed.slice(firstBrace, lastBrace + 1));
  }

  return candidates;
};

export function parseWikiDraftResponse(raw: string): WikiDraftPayload | null {
  for (const candidate of extractJsonCandidates(raw)) {
    const parsed = tryParseObject(candidate);
    if (!parsed) continue;

    const payload: WikiDraftPayload = {
      title: asTrimmedString(parsed.title),
      type: asTrimmedString(parsed.type),
      summary: asTrimmedString(parsed.summary),
      content: asTrimmedString(parsed.content),
      tags: normalizeTags(parsed.tags),
      templateData: normalizeTemplateData(parsed.templateData),
    };

    const hasContent =
      payload.title.length > 0
      || payload.type.length > 0
      || payload.summary.length > 0
      || payload.content.length > 0
      || payload.tags.length > 0
      || Object.keys(payload.templateData).length > 0;

    if (hasContent) {
      return payload;
    }
  }

  return null;
}
